import React from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as PayPalLogo } from '../../../../assets/images/icons/paypal.svg';
import CreditCardLogo from '../../../../components/CreditCardLogo/CreditCardLogo';
import useCheckoutPaths from '../../../../hooks/useCheckoutPaths';
import { expectUnreachable } from '../../../../lib/util';
import styles from '../../../../styles/lineItem.module.scss';
import * as types from '../../../../types';
import { getFiGrowthBook } from '../../../../lib/growthbook';
import classNames from 'classnames';

interface BillingLineItemProps {
  billingInfo: types.BillingInfo;
}

function PayPalBillingInfo() {
  const { t } = useTranslation();
  const showCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);

  return (
    <div className={styles.lineItem}>
      <div className={styles.itemRow}>
        <div className={classNames(styles.lineItemImage, { [styles.greyBackground]: showCartTest })}>
          <PayPalLogo />
        </div>
        <div className={styles.lineItemBody}>
          <div className={styles.lineItemPriceRows}>{t('pay_with_paypal')}</div>
        </div>
      </div>
    </div>
  );
}

function CreditCardBillingInfo({ type, lastFour, month, year }: types.CreditCardInfo) {
  const { t } = useTranslation();
  const checkoutPaths = useCheckoutPaths();
  const showCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
  const useStripeCheckout = getFiGrowthBook().getFeatureValue('stripe-elements-checkout', false);
  const editPath = useStripeCheckout ? checkoutPaths.StripeCheckout : checkoutPaths.Payment;

  return (
    <div className={styles.lineItem}>
      <div className={styles.itemRow}>
        <div className={classNames(styles.lineItemImage, { [styles.greyBackground]: showCartTest })}>
          <CreditCardLogo cardType={type} />
        </div>
        <div className={styles.lineItemBody}>
          <div className={styles.lineItemPriceRows}>
            {/* sentry-mask class is used to mask text in Sentry replays */}
            <div>
              <Trans
                i18nKey="type_ending_in_last_four_mask"
                values={{
                  type: type,
                  lastFour: lastFour,
                }}
                components={{ mask: <span className="sentry-mask" /> }}
              />
            </div>
            <div>
              <Trans
                i18nKey="expires_month_year"
                values={{
                  month: month,
                  year: year,
                }}
                components={{ mask: <span className="sentry-mask" /> }}
              />
            </div>
          </div>
          <div className={styles.removeButtonRow}>
            <Link to={editPath}>{t('edit')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function BankAccountBillingInfo({ bankName, last4 }: types.BankAccountInfo) {
  const { t } = useTranslation();
  const checkoutPaths = useCheckoutPaths();

  return (
    <div className={styles.lineItem}>
      <div className={styles.itemRow}>
        <div className={styles.lineItemBody}>
          <div className={styles.lineItemPriceRows}>
            <div>
              <span className="sentry-mask">
                {bankName ? `${bankName} ` : ''}
                {last4 ? t('bank_account_ending_in', { last4 }) : t('bank_account')}
              </span>
            </div>
          </div>
          <div className={styles.removeButtonRow}>
            <Link to={checkoutPaths.StripeCheckout}>{t('edit')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BillingLineItem({ billingInfo: { paymentInfo } }: BillingLineItemProps) {
  if (paymentInfo.__typename === 'ObfuscatedCardInfo') {
    return <CreditCardBillingInfo {...paymentInfo} />;
  } else if (paymentInfo.__typename === 'PayPalInfo') {
    return <PayPalBillingInfo />;
  } else if (paymentInfo.__typename === 'BankAccountInfo') {
    return <BankAccountBillingInfo {...paymentInfo} />;
  } else {
    expectUnreachable(paymentInfo);
    return null;
  }
}
